<template>
    <v-dialog v-model="restoreDialog" max-width="700" persistent>
        <template v-slot:activator="{ on: dialog, attrs }">
            <template v-if="$scopedSlots.activator">
                <slot name="activator" :on="dialog" :attrs="attrs" :open="openDialog"></slot>
            </template>
            <v-tooltip v-else top>
                <template v-slot:activator="{ on: tooltip }">
                    <v-btn v-on="{ ...tooltip, ...dialog }" icon small :color="isRestoreToCurrentInstance ? 'primary' : ''">
                        <v-icon small v-if="isRestoreToCurrentInstance">restore</v-icon>
                        <v-icon small v-else>group</v-icon>
                    </v-btn>
                </template>
                <span v-if="isRestoreToCurrentInstance">Reset the current state of this instance</span>
                <span v-else>Reset the current state of a target instance</span>
            </v-tooltip>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <span class="ml-1 secondary--text">Snapshot Reset</span>
                <v-spacer></v-spacer>
                <v-btn :disabled="restoringSnapshot" @click="restoreDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-banner v-if="isColdSnapshot">
                    <v-avatar slot="icon" color="white" size="30"><v-icon color="info">info</v-icon></v-avatar>
                    This snapshot is in cold storage, so resetting the instance might take longer than usual.
                </v-banner>
                <div v-if="snapshotData">
                    <div>
                        <div>
                            <span>Snapshot name</span>
                            :
                            <span class="font-weight-bold">{{ snapshotData.long_id }}</span>
                        </div>
                        <div>
                            <span>Snapshot timestamp</span>
                            :
                            <span class="font-weight-bold">{{ snapshotData.snapshot_timestamp | dateTimeToHuman }}</span>
                        </div>
                    </div>
                    <v-alert text type="info" class="mt-4">
                        <v-row align="center">
                            <v-col class="grow">
                                <div class="font-weight-bold">Did you know?</div>
                                <div>You can reset partially too.</div>
                            </v-col>
                            <v-col class="shrink">
                                <v-btn
                                    href="https://docs.nuvolos.com/how-to-guides/common-workflows/snapshots/restore-a-snapshot"
                                    target="_blank"
                                    outlined
                                    small
                                    color="info">
                                    Learn more
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-alert>
                    <div v-if="isRestoreToCurrentInstance">
                        <v-alert color="error" prominent type="warning" class="mt-5" text>
                            <span class="font-weight-bold">Here is what happens when you reset the current state to a snapshot</span>
                            <ul class="mt-3">
                                <li class="subtitle-2">
                                    The
                                    <span class="text-decoration-underline font-weight-bold">current state</span>
                                    of this instance will be be overwritten with the contents of the snapshot
                                    <span class="text-decoration-underline font-weight-bold">{{ snapshotData.long_id }}</span>
                                    .
                                </li>
                                <li class="subtitle-2">
                                    An auto snapshot of the
                                    <span class="text-decoration-underline font-weight-bold">current state</span>
                                    of this instance will be created (to be able to revert this operation).
                                </li>
                            </ul>
                            <v-divider class="mt-4 error"></v-divider>
                            <v-checkbox light color="error" v-model="agreeBox">
                                <template v-slot:label>
                                    <span>I understand</span>
                                </template>
                            </v-checkbox>
                        </v-alert>
                    </div>
                    <div v-else>
                        <v-select
                            class="mt-8"
                            :items="instanceNames"
                            v-model="selectedInstance"
                            label="Select target instance to overwrite"
                            :disabled="restoring || !sourceSnapshotName"
                            outlined></v-select>
                        <v-alert v-if="selectedInstance" color="error" prominent type="warning" text class="mb-0">
                            <span class="font-weight-bold">Here is what happens when you overwrite the current state of a target instance</span>
                            <ul class="mt-3">
                                <li class="subtitle-2">
                                    The contents (files, tables, and application) of the current state of the instance
                                    <span class="text-decoration-underline font-weight-bold">{{ selectedInstance }}</span>
                                    will be cleared and replaced with the contents of
                                    <span class="text-decoration-underline font-weight-bold">{{ sourceSnapshotName }}.</span>
                                </li>
                                <li class="subtitle-2">
                                    A restorable auto snapshot of the current state of
                                    <span class="text-decoration-underline font-weight-bold">{{ selectedInstance }}</span>
                                    will be created (to be able to revert this operation).
                                </li>
                            </ul>
                            <v-divider class="mt-4 error"></v-divider>
                            <v-checkbox light color="error" v-model="agreeBox">
                                <template v-slot:label>
                                    <span>I understand</span>
                                </template>
                            </v-checkbox>
                        </v-alert>
                    </div>
                </div>
                <v-checkbox
                    v-if="isRestoreToCurrentInstance && !isInstanceArchived"
                    class="ma-0 pa-0"
                    v-model="createBackupSnapshot"
                    label="Create a backup snapshot of the current state before resetting"></v-checkbox>
                <v-alert :value="error" color="error" prominent type="error" class="mt-5" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Snapshot Reset Error</span>
                        An error has occurred and the instance could not be reset. Please try again later and if the problem persists contact
                        support@alphacruncher.com
                    </div>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="reviewSnapshot" text :disabled="restoringSnapshot || restoring">review snapshot</v-btn>
                <v-btn
                    v-if="isRestoreToCurrentInstance"
                    text
                    class="mr-1"
                    color="error"
                    :loading="restoringSnapshot"
                    :disabled="restoringSnapshot || !agreeBox"
                    @click="restoreSnapshotToCurrentState">
                    Reset
                </v-btn>
                <v-btn
                    v-else
                    color="error"
                    class="mr-1"
                    text
                    :loading="restoring"
                    @click="overwriteCurrentState"
                    :disabled="restoring || !selectedInstance.length || !agreeBox">
                    overwrite
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { isEditorRole, isMasterOrDistributedInstance } from '@/utils'

export default {
    name: 'SnapshotRestoreDialog',
    props: {
        snapshotData: Object,
        isColdSnapshot: Boolean,
        isRestoreToCurrentInstance: Boolean
    },
    data() {
        return {
            error: false,
            createBackupSnapshot: true,
            agreeBox: false,
            restoring: false,
            selectedInstance: '',
            restoreDialog: false
        }
    },
    methods: {
        openDialog() {
            this.restoreDialog = true
        },
        restoreSnapshotToCurrentState: function () {
            const fetchBody = { snid: this.snapshotData.snid }
            if (this.isInstanceArchived) {
                fetchBody.createBackup = false
            } else {
                fetchBody.createBackup = this.createBackupSnapshot
            }
            this.$store.dispatch('snapshotStore/restoreSnapshotToCurrentState', fetchBody).finally(() => {
                this.restoreDialog = false
            })
        },
        overwriteCurrentState: function () {
            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Overwriting target, please wait....',
                snackBarTimeout: 10000,
                snackBarIcon: 'info'
            })
            this.error = false
            this.restoring = true
            const iid = this.spaceInstances.find(instance => instance.long_id.toUpperCase() === this.selectedInstance.toUpperCase()).iid
            this.$axios
                .put(`snapshots/${this.snapshotData.snid}/load_to/${iid}/async`, {})
                .then(response => {
                    this.restoreDialog = false
                    this.$store.dispatch('showGlobalDialog', {
                        dialogText: 'Loading of snapshot into instance has started, see the status below',
                        dialogTitle: 'Loading snapshot into existing instance',
                        dialogAction: ['dismiss'],
                        taskId: response.data.tkid
                    })
                })
                .catch(() => {
                    this.error = true
                })
                .finally(() => {
                    this.restoring = false
                })
        },
        reviewSnapshot: function () {
            this.$router.push({
                name: 'snapshot-overview',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid,
                    iid: this.$route.params.iid,
                    snid: this.snapshotData.snid
                }
            })
        }
    },
    computed: {
        ...mapState('snapshotStore', ['restoringSnapshot']),
        ...mapState('spaceStore', ['spaceInstances']),
        ...mapGetters('instanceStore', ['isInstanceArchived']),
        sourceSnapshotName: function () {
            return this.$store.getters['snapshotStore/snapshotLongNameById'](this.snapshotData.snid)
        },
        instanceNames: function () {
            const editorInstances = this.spaceInstances.filter(instance => isEditorRole(instance.role))
            const instances = editorInstances.map(instance => instance.long_id)
            return instances.filter(instance => !isMasterOrDistributedInstance(instance))
        }
    },
    watch: {
        restoreDialog: function (nextVal) {
            if (!nextVal && this.error) {
                this.error = false
            }
        }
    }
}
</script>
